import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Message } from '../../types/chat';
import FileMessage from '../../components/FileMessage';

const MessageListContainer = styled.div<{ $isAdminView?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isAdminView ? '2px' : '7px'};
  padding: ${props => props.$isAdminView ? '4px 4px 2px 4px' : '10px 10px 4px 10px'};
  overflow-y: auto;
  height: 100%;
  background: transparent;
`;

const MessageItem = styled.div<{ $isOwn: boolean; $isAdminView?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isOwn ? 'flex-end' : 'flex-start'};
  max-width: 90%;
  margin: ${props => props.$isOwn ? '0 0 0 auto' : '0 auto 0 0'};
  padding: ${props => props.$isAdminView ? '1px 2px' : '4px 5px'};
`;

const getMessageBackground = (isAdminView?: boolean, isOwn?: boolean): string => {
  if (isAdminView) {
    return isOwn ? '#111827' : '#e2e8f0';
  }
  return isOwn
    ? 'linear-gradient(145deg, rgba(37, 37, 37, 0.95) 0%, rgba(0, 0, 0, 0.9) 100%)'
    : 'rgba(255, 255, 255, 0.1)';
};

const getMessageColor = (isAdminView?: boolean, isOwn?: boolean): string => {
  if (isAdminView) {
    return isOwn ? '#f8fafc' : '#0f172a';
  }
  return isOwn ? '#f2f2f2' : '#f5f5f5';
};

const MessageContent = styled.div<{ $isOwn: boolean; $isAdminView?: boolean }>`
  background: ${props => getMessageBackground(props.$isAdminView, props.$isOwn)};
  color: ${props => getMessageColor(props.$isAdminView, props.$isOwn)};
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  margin-bottom: 1px;
  word-break: break-word;
  transition: background 0.2s;
  position: relative;
`;

const HoverTime = styled.span<{ $isOwn: boolean }>`
  display: none;
  position: absolute;
  top: 50%;
  ${props => (props.$isOwn ? 'left: -4.5rem;' : 'right: -4.5rem;')}
  transform: translateY(-50%);
  background-color: #333;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
`;

const MessageWrapper = styled.div`
  position: relative;
  &:hover ${HoverTime} {
    display: block;
  }
`;

const DateSeparator = styled.div<{ $isAdminView?: boolean }>`
  text-align: center;
  margin: 1rem 0;
  color: ${props => props.$isAdminView ? '#64748b' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 0.8rem;
  font-weight: bold;
`;

const MessageHeader = styled.div<{ $isAdminView?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  font-size: 0.8em;
  color: ${props => props.$isAdminView ? '#334155' : 'rgba(255, 255, 255, 0.84)'};
  font-weight: 700;
`;

const MessageSender = styled.span<{ $isAdminView?: boolean }>`
  font-weight: bold;
  color: ${props => props.$isAdminView ? '#0f172a' : 'rgba(255, 255, 255, 0.9)'};
  margin-right: 6px;
  font-size: 0.8em;
`;

const MessageTime = styled.span<{ $isAdminView?: boolean }>`
  color: ${props => props.$isAdminView ? '#475569' : 'rgba(255, 255, 255, 0.62)'};
  font-size: 0.85em;
  display: ${props => props.$isAdminView ? 'inline' : 'none'};
`;

const MessageStatus = styled.div<{ $isAdminView?: boolean; $status?: string }>`
  font-size: 0.8em;
  color: ${props => props.$isAdminView ? '#475569' : 'rgba(255, 255, 255, 0.75)'};
  margin-top: 4px;
  text-align: right;
  display: ${props => props.$isAdminView && props.$status === 'read' ? 'none' : 'block'};
`;

const TypingIndicator = styled.div`
  font-size: 0.95em;
  color: rgba(255, 255, 255, 0.9);
  font-style: italic;
  padding: 7px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  margin: 5px 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
`;

const MessageBadge = styled.div`
  display: inline-block;
  min-width: 18px;
  padding: 0 5px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  text-align: center;
`;

interface MessageListProps {
  messages: Message[];
  currentUserId: string;
  typingUsers: string[];
  isAdminView?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, currentUserId, typingUsers, isAdminView, onLoadMore, hasMore }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current?.scrollTop === 0) {
        onLoadMore?.();
      }
    };
    const ref = containerRef.current;
    if (ref) ref.addEventListener('scroll', handleScroll);
    return () => { if (ref) ref.removeEventListener('scroll', handleScroll); };
  }, [onLoadMore]);

  const formatTime = (timestamp?: string) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const decodeName = (name: string) => {
    try {
      return decodeURIComponent(name);
    } catch {
      return name;
    }
  };

  // Filter typingUsers: do not show the current user (userId)
  const filteredTypingUsers = typingUsers.filter(id => id !== currentUserId);

  // Function to get the correct file URL considering proxying
  const getFileUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('/uploads/')) {
      const filename = url.split('/').pop();
      const base = (globalThis as any).supportlyChatConfig?.staticUrl
        || process.env.REACT_APP_API_URL
        || globalThis.location.origin;
      return base.replace(/\/$/, '') + '/api/messages/download/' + filename;
    }
    return url;
  };

  const formatDateSeparator = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Vandaag';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Gisteren';
    }
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const validMessages = messages.filter(msg => msg.timestamp && !Number.isNaN(new Date(msg.timestamp).getTime()));

  const messagesByDate = validMessages.reduce((acc, message) => {
    const date = new Date(message.timestamp).toDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(message);
    return acc;
  }, {} as Record<string, Message[]>);

  return (
    <MessageListContainer ref={containerRef} $isAdminView={isAdminView}>
      {hasMore && (
        <div style={{ textAlign: 'center', color: '#888', margin: '10px 0' }}>Meer berichten laden...</div>
      )}
      {validMessages.length === 0 && (
        <div style={{ textAlign: 'center', color: '#bdbdbd', margin: '20px 0' }}>Geen berichten</div>
      )}
      {Object.keys(messagesByDate).map(date => (
        <React.Fragment key={date}>
          <DateSeparator $isAdminView={isAdminView}>{formatDateSeparator(date)}</DateSeparator>
          {messagesByDate[date].map((message, index) => {
            const isFile = !!message.fileUrl || (message as any).type === 'file' || !!message.fileName;
            if (isFile) {
              const fileProps = {
                name: message.fileName || '',
                type: message.fileType || '',
                size: message.fileSize || 0,
                url: getFileUrl(message.fileUrl || '')
              };
              return (
                <MessageItem key={message.id || `${message.timestamp}-${message.sender}-${index}`} $isOwn={message.sender === currentUserId} $isAdminView={isAdminView}>
                  <MessageHeader $isAdminView={isAdminView}>
                    <MessageSender $isAdminView={isAdminView}>{decodeName(message.senderName)}</MessageSender>
                    <MessageTime $isAdminView={true}>{formatTime(message.timestamp)}</MessageTime>
                  </MessageHeader>
                  <MessageWrapper>
                    <FileMessage
                      file={fileProps}
                      timestamp={message.timestamp ? new Date(message.timestamp) : new Date()}
                      isUser={message.sender === currentUserId}
                    />
                  </MessageWrapper>
                  <MessageStatus $isAdminView={isAdminView} $status={message.status}>
                    {message.status === 'sending' && 'Verzenden...'}
                    {message.status === 'sent' && 'Verzonden'}
                    {message.status === 'delivered' && 'Afgeleverd'}
                    {message.status === 'read' && 'Gelezen'}
                    {message.status === 'error' && 'Verzendfout'}
                  </MessageStatus>
                </MessageItem>
              );
            }
            return (
              <MessageItem key={message.id || `${message.timestamp}-${message.sender}-${index}`} $isOwn={message.sender === currentUserId} $isAdminView={isAdminView}>
                <MessageHeader $isAdminView={isAdminView}>
                  <MessageSender $isAdminView={isAdminView}>{decodeName(message.senderName)}</MessageSender>
                  <MessageTime $isAdminView={true}>{formatTime(message.timestamp)}</MessageTime>
                </MessageHeader>
                <MessageWrapper>
                  <MessageContent $isOwn={message.sender === currentUserId} $isAdminView={isAdminView}>
                    {message.content}
                  </MessageContent>
                </MessageWrapper>
                <MessageStatus $isAdminView={isAdminView} $status={message.status}>
                  {message.status === 'sending' && 'Verzenden...'}
                  {message.status === 'sent' && 'Verzonden'}
                  {message.status === 'delivered' && 'Afgeleverd'}
                  {message.status === 'read' && 'Gelezen'}
                  {message.status === 'error' && 'Verzendfout'}
                </MessageStatus>
              </MessageItem>
            );
          })}
        </React.Fragment>
      ))}
      {filteredTypingUsers.length > 0 && (
        <TypingIndicator>
          {filteredTypingUsers.join(', ')} {filteredTypingUsers.length === 1 ? 'is aan het typen' : 'zijn aan het typen'}...
        </TypingIndicator>
      )}
      <div ref={endRef} />
    </MessageListContainer>
  );
}; 
